import { asbWarn } from '@project/common/util/log';
import sanitize from 'sanitize-filename';
export { arrayEquals } from '@project/common/util/array-equals'; // Necessary to break an import cycle between settings and util
import { arrayEquals } from '@project/common/util/array-equals';
import type {
    DimensionsModel,
    Rgb,
    SubtitleModel,
    SubtitleTextImage,
    SubtitleTrack,
    Token,
    Tokenization,
    TokenReading,
} from '@project/common/src/model';
import type { SeekableTracks, TextSubtitleSettings } from '@project/common/settings';
import { isTrackSeekable, TokenStatus } from '@project/common/settings';
import type { Progress } from '..';
import type { TokenStatusInfo } from '@project/common/dictionary-db';
import type { PitchAccentPosition } from '@project/common/yomitan';

export interface AnimationFrameRetryOptions {
    runImmediately?: boolean;
}

/** Runs an operation until it succeeds or its animation-frame attempt limit is reached. */
export const retryWithAnimationFrame = (
    operation: () => boolean,
    maxAttempts: number,
    { runImmediately = false }: AnimationFrameRetryOptions = {}
): (() => void) => {
    let animationFrame: number | undefined;
    let attempts = 0;
    let cancelled = false;

    const attempt = () => {
        animationFrame = undefined;
        if (cancelled || operation() || ++attempts >= maxAttempts) return;
        animationFrame = requestAnimationFrame(attempt);
    };

    if (maxAttempts > 0) {
        if (runImmediately) attempt();
        else animationFrame = requestAnimationFrame(attempt);
    }

    return () => {
        cancelled = true;
        if (animationFrame !== undefined) {
            cancelAnimationFrame(animationFrame);
            animationFrame = undefined;
        }
    };
};

let subtitleHtmlHelperElement: HTMLDivElement | undefined;
const subtitleGraphemeSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
const invisibleGraphemePattern = /^[\s\p{Default_Ignorable_Code_Point}]*$/u;

/** Removes presentation markup, ruby readings, and ruby fallback text from subtitle text. */
export const removeSubtitleHtml = (text: string): string => {
    subtitleHtmlHelperElement ??= document.createElement('div');
    subtitleHtmlHelperElement.innerHTML = text;
    for (const element of subtitleHtmlHelperElement.querySelectorAll('br')) element.replaceWith('\n');
    for (const element of subtitleHtmlHelperElement.querySelectorAll('rt, rp')) element.remove();
    return subtitleHtmlHelperElement.textContent ?? subtitleHtmlHelperElement.innerText;
};

/** Counts visible subtitle graphemes, excluding markup, ruby annotations, whitespace, and formatting controls. */
export const readableCharacterCount = (text: string): number => {
    const readableText = removeSubtitleHtml(text).normalize('NFC');
    let count = 0;
    for (const { segment } of subtitleGraphemeSegmenter.segment(readableText)) {
        if (!invisibleGraphemePattern.test(segment)) count++;
    }
    return count;
};

// Cues on the same track can share a start time (e.g. Netflix splitting one line into
// multiple cues), and SubtitleCollection does not guarantee source order in that case, so
// callers displaying subtitles should sort by track and fall back to source index for ties.
export function compareSubtitlesForDisplay(
    s1: Pick<SubtitleModel, 'track' | 'index'>,
    s2: Pick<SubtitleModel, 'track' | 'index'>
): number {
    return s1.track - s2.track || (s1.index ?? 0) - (s2.index ?? 0);
}

/** Maps separately sorted showing and invisible subtitles into canonical display order. */
export function mapSubtitlesForDisplay<T extends Pick<SubtitleModel, 'track' | 'index'>, R>(
    showingSubtitles: readonly T[],
    invisibleSubtitles: readonly T[],
    map: (subtitle: T, visible: boolean, sourceIndex: number) => R
): R[] {
    const result: R[] = [];
    let showingIndex = 0;
    let invisibleIndex = 0;
    while (showingIndex < showingSubtitles.length || invisibleIndex < invisibleSubtitles.length) {
        const showingSubtitle = showingSubtitles[showingIndex];
        const invisibleSubtitle = invisibleSubtitles[invisibleIndex];
        if (
            invisibleSubtitle === undefined ||
            (showingSubtitle !== undefined && compareSubtitlesForDisplay(showingSubtitle, invisibleSubtitle) < 0)
        ) {
            result.push(map(showingSubtitle, true, showingIndex));
            showingIndex++;
        } else {
            result.push(map(invisibleSubtitle, false, invisibleIndex));
            invisibleIndex++;
        }
    }
    return result;
}

export function keysAreEqual(a: any, b: any) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => Object.prototype.hasOwnProperty.call(b, key));
}

export interface LocalizeDateTimeOptions {
    locales?: Intl.LocalesArgument;
    timeZone?: string;
    hour12?: boolean;
    includeMilliseconds?: boolean;
    includeDate?: boolean;
}

export const localizeDateTime = (timestamp: number, options: LocalizeDateTimeOptions = {}) => {
    const date = new Date(timestamp);
    const locales = options.locales ?? [];
    const time = date.toLocaleTimeString(locales, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: options.hour12,
        fractionalSecondDigits: options.includeMilliseconds ? 3 : undefined,
        timeZone: options.timeZone,
    });
    if (!options.includeDate) return time;

    const calendarDate = date.toLocaleDateString(locales, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: options.timeZone,
    });
    return calendarDate + ' ' + time;
};

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function utcStartOfToday(): Date {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

export function humanReadableTime(timestamp: number, nearestTenth = false, fullyPadded = false): string {
    const totalSeconds = Math.floor(timestamp / 1000);
    let seconds;

    if (nearestTenth) {
        seconds = Math.round(((timestamp / 1000) % 60) * 10) / 10;
    } else {
        seconds = totalSeconds % 60;
    }

    const minutes = Math.floor(totalSeconds / 60) % 60;
    const hours = Math.floor(totalSeconds / 3600);

    if (fullyPadded) {
        let secondsStr: string;
        if (nearestTenth) {
            // For decimal seconds, pad integer part to 2 digits (e.g., 8.2 -> 08.2, 1 -> 01.0)
            const secondsParts = String(seconds).split('.');
            const integerPart = secondsParts[0];
            const decimalPart = secondsParts.length > 1 ? '.' + secondsParts[1] : '.0';
            secondsStr = integerPart.padStart(2, '0') + decimalPart;
        } else {
            secondsStr = String(seconds).padStart(2, '0');
        }
        return String(hours).padStart(2, '0') + 'h' + String(minutes).padStart(2, '0') + 'm' + secondsStr + 's';
    } else {
        if (hours > 0) {
            return hours + 'h' + String(minutes).padStart(2, '0') + 'm' + String(seconds).padStart(2, '0') + 's';
        }

        return minutes + 'm' + String(seconds).padStart(2, '0') + 's';
    }
}

export function formatAsSigned(value: number, decimalPlaces?: number): string {
    const stringValue = decimalPlaces === undefined ? String(value) : value.toFixed(decimalPlaces);
    return value >= 0 ? `+${stringValue}` : stringValue;
}

export function formatAsSignedMs(milliseconds: number): string {
    return `${formatAsSigned(milliseconds)} ms`;
}

export function timeDurationDisplay(
    milliseconds: number,
    totalMilliseconds: number,
    includeMilliseconds = true
): string {
    milliseconds = Math.round(milliseconds);
    const sign = milliseconds < 0 ? '-' : '';
    milliseconds = Math.abs(milliseconds);
    const includeHours = totalMilliseconds >= 3600000 || milliseconds >= 3600000;
    const remainingMilliseconds = milliseconds % 1000;
    milliseconds = (milliseconds - remainingMilliseconds) / 1000;
    const seconds = milliseconds % 60;
    milliseconds = (milliseconds - seconds) / 60;
    const minutes = milliseconds % 60;

    if (includeHours) {
        const hours = (milliseconds - minutes) / 60;

        if (includeMilliseconds) {
            return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(remainingMilliseconds).padStart(3, '0')}`;
        }

        return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    if (includeMilliseconds) {
        return `${sign}${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(remainingMilliseconds).padStart(3, '0')}`;
    }

    return `${sign}${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function clampMediaTimestamp(timestamp: number, mediaLength?: number): number {
    const clampedTimestamp = Math.max(0, timestamp);
    if (mediaLength === undefined || !Number.isFinite(mediaLength) || mediaLength <= 0) return clampedTimestamp;
    return Math.min(clampedTimestamp, mediaLength);
}

export const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

export function normalizeFinite(value: number): number;
export function normalizeFinite(value: number, fallback: number): number;
export function normalizeFinite(value: number, fallback: undefined): number | undefined;
export function normalizeFinite(value: number, fallback?: number): number | undefined {
    return Number.isFinite(value) ? value : arguments.length > 1 ? fallback : 0;
}

export const normalizeNonNegative = (value: number): number => Math.max(0, normalizeFinite(value));

export const normalizeNonPositive = (value: number): number => Math.min(0, normalizeFinite(value));

export function getCurrentTimeString(): string {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}-${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}`;
}

export function adjacentSubtitle(
    forward: boolean,
    time: number,
    subtitles: SubtitleModel[],
    seekableTracks: SeekableTracks
) {
    const now = time;
    let adjacentSubtitleIndex = -1;
    let minDiff = Number.MAX_SAFE_INTEGER;

    if (forward) {
        for (let i = 0; i < subtitles.length; ++i) {
            const s = subtitles[i];
            if (!isTrackSeekable(seekableTracks, s.track)) continue;

            const diff = s.start - now;
            if (minDiff <= diff) continue;

            if (now < s.start) {
                minDiff = diff;
                adjacentSubtitleIndex = i;
            }
        }
    } else {
        for (let i = subtitles.length - 1; i >= 0; --i) {
            const s = subtitles[i];
            if (!isTrackSeekable(seekableTracks, s.track)) continue;

            const diff = now - s.end;
            if (minDiff <= diff) continue;

            if (now >= s.end) {
                minDiff = diff;
                adjacentSubtitleIndex = i;
            }
        }
    }

    if (adjacentSubtitleIndex !== -1) return subtitles[adjacentSubtitleIndex];
    return null;
}

export function surroundingSubtitles(
    subtitles: SubtitleModel[],
    index: number,
    countRadius: number,
    timeRadius: number
): SubtitleModel[] {
    let startIndex = index;

    for (let i = index; i >= 0; --i) {
        startIndex = i;

        if (atBoundary(subtitles, startIndex, index, countRadius, timeRadius, Direction.backward)) {
            break;
        }
    }

    let endIndex = startIndex;

    for (let i = index; i <= subtitles.length - 1; ++i) {
        endIndex = i;

        if (atBoundary(subtitles, endIndex, index, countRadius, timeRadius, Direction.forward)) {
            break;
        }
    }

    return subtitles.slice(startIndex, endIndex + 1);
}

function indexNearTimestamp(subtitles: SubtitleModel[], timestamp: number, direction: Direction) {
    if (direction === Direction.forward) {
        for (let i = 0; i < subtitles.length; ++i) {
            if (subtitles[i].start >= timestamp) {
                return i;
            }
        }
    } else {
        for (let i = subtitles.length - 1; i >= 0; --i) {
            if (subtitles[i].start <= timestamp) {
                return i;
            }
        }
    }

    return undefined;
}

export function surroundingSubtitlesAroundInterval(
    subtitles: SubtitleModel[],
    startTimestamp: number,
    endTimestamp: number,
    countRadius: number,
    timeRadius: number
) {
    if (subtitles.length === 0) {
        return {};
    }

    let startBoundaryIndex = 0;
    const indexAfterStartTimestamp =
        indexNearTimestamp(subtitles, startTimestamp, Direction.forward) ?? subtitles.length - 1;

    for (let i = 0; i < subtitles.length; ++i) {
        startBoundaryIndex = i;

        if (
            withinBoundaryAroundInterval(
                subtitles,
                i,
                countRadius,
                timeRadius,
                startTimestamp,
                indexAfterStartTimestamp
            )
        ) {
            break;
        }
    }

    let endBoundaryIndex = subtitles.length - 1;
    const indexBeforeEndTimestamp = indexNearTimestamp(subtitles, endTimestamp, Direction.backward) ?? 0;

    for (let i = subtitles.length - 1; i >= 0; --i) {
        endBoundaryIndex = i;

        if (
            withinBoundaryAroundInterval(subtitles, i, countRadius, timeRadius, endTimestamp, indexBeforeEndTimestamp)
        ) {
            break;
        }
    }

    if (endBoundaryIndex <= startBoundaryIndex) {
        return {};
    }

    return {
        surroundingSubtitles: subtitles.slice(startBoundaryIndex, endBoundaryIndex + 1),
        subtitle: subtitles[indexAfterStartTimestamp],
    };
}

export function mockSurroundingSubtitles(
    middleSubtitle: SubtitleModel,
    maxTimestamp: number,
    timeRadius: number
): SubtitleModel[] {
    const subtitles = [middleSubtitle];
    const offset = middleSubtitle.start - middleSubtitle.originalStart;

    if (middleSubtitle.end < maxTimestamp) {
        const afterTimestamp = Math.min(maxTimestamp, middleSubtitle.end + timeRadius);
        subtitles.push({
            text: '',
            start: middleSubtitle.end,
            end: afterTimestamp,
            originalStart: middleSubtitle.end - offset,
            originalEnd: afterTimestamp - offset,
            track: middleSubtitle.track,
            index: middleSubtitle.index,
        });
    }

    if (middleSubtitle.start > 0) {
        const beforeTimestamp = Math.max(0, middleSubtitle.start - timeRadius);
        subtitles.unshift({
            text: '',
            start: beforeTimestamp,
            end: middleSubtitle.start,
            originalStart: beforeTimestamp - offset,
            originalEnd: middleSubtitle.start - offset,
            track: middleSubtitle.track,
            index: middleSubtitle.index,
        });
    }

    return subtitles;
}

enum Direction {
    forward,
    backward,
}

function atBoundary(
    subtitles: SubtitleModel[],
    index: number,
    initialIndex: number,
    countRadius: number,
    timeRadius: number,
    direction: Direction
): boolean {
    let next;

    if (direction == Direction.forward) {
        next = index + 1 < subtitles.length ? subtitles[index + 1] : null;
    } else {
        next = index - 1 >= 0 ? subtitles[index - 1] : null;
    }

    if (
        Math.abs(initialIndex - index) >= countRadius &&
        (next === null || Math.abs(next.start - subtitles[initialIndex].start) >= timeRadius)
    ) {
        return true;
    }

    return false;
}

function withinBoundaryAroundInterval(
    subtitles: SubtitleModel[],
    index: number,
    countRadius: number,
    timeRadius: number,
    timestamp: number,
    indexNearTimestamp: number
): boolean {
    const current = subtitles[index];

    if (Math.abs(indexNearTimestamp - index) <= countRadius || Math.abs(current.start - timestamp) <= timeRadius) {
        return true;
    }

    return false;
}

export function errorMessageFromVideo(element: HTMLMediaElement): string {
    let error: string;
    switch (element.error?.code) {
        case 1:
            error = 'MEDIA_ERR_ABORTED';
            break;
        case 2:
            error = 'MEDIA_ERR_ABORTED';
            break;
        case 3:
            error = 'MEDIA_ERR_DECODE';
            break;
        case 4:
            error = 'MEDIA_ERR_SRC_NOT_SUPPORTED';
            break;
        default:
            error = 'Unknown error';
            break;
    }
    return error + ': ' + (element.error?.message || '<details missing>');
}

export function subtitleTimestampWithDelay(subtitle: Pick<SubtitleModel, 'start' | 'end'>, delay: number): number {
    const start = Math.min(subtitle.start, subtitle.end);
    const end = Math.max(subtitle.start, subtitle.end);

    return Math.max(start, Math.min(end, delay >= 0 ? start + delay : end + delay));
}

export function subtitleIntersectsTimeInterval(subtitle: SubtitleModel, interval: number[]) {
    const length = Math.max(0, subtitle.end - subtitle.start);

    if (length === 0) {
        return false;
    }

    const overlapStart = Math.max(subtitle.start, interval[0]);
    const overlapEnd = Math.min(subtitle.end, interval[1]);

    return overlapEnd - overlapStart >= length / 2;
}

export function joinSubtitles(subtitles: SubtitleModel[]) {
    return subtitles
        .filter((s) => s.text.trim() !== '')
        .map((s) => s.text)
        .join('\n');
}

export function extractText(subtitle: SubtitleModel, surroundingSubtitles: SubtitleModel[], track?: number) {
    if (surroundingSubtitles.length === 0) {
        return subtitle.text;
    }

    const interval = [subtitle.start, subtitle.end];
    return joinSubtitles(
        surroundingSubtitles
            .filter((s) => subtitleIntersectsTimeInterval(s, interval))
            .filter((s) => track === undefined || s.track === track)
    );
}

export function download(blob: Blob, name: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = url;
    a.download = sanitize(name);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
}

export function computeStyles(
    {
        subtitleColor,
        subtitleSize,
        subtitleThickness,
        subtitleOutlineThickness,
        subtitleOutlineColor,
        subtitleShadowThickness,
        subtitleShadowColor,
        subtitleBackgroundOpacity,
        subtitleBackgroundColor,
        subtitleFontFamily,
        subtitleCustomStyles,
    }: TextSubtitleSettings,
    values: { [key: string]: string | number } = {}
) {
    const styles: { [key: string]: any } = {
        ...values,
        color: subtitleColor,
        fontSize: `${subtitleSize}px`,
        fontWeight: String(subtitleThickness),
        WebkitTextStroke: '0 transparent',
        textShadow: 'none',
    };

    if (subtitleOutlineThickness > 0) {
        const thickness = subtitleOutlineThickness;
        const color = subtitleOutlineColor;
        styles['WebkitTextStroke'] = `${color} ${thickness}px`;
        styles['paintOrder'] = `stroke fill`;
    }

    if (subtitleShadowThickness > 0) {
        styles['textShadow'] =
            `0 0 ${subtitleShadowThickness}px ${subtitleShadowColor}, 0 0 ${subtitleShadowThickness}px ${subtitleShadowColor}, 0 0 ${subtitleShadowThickness}px ${subtitleShadowColor}, 0 0 ${subtitleShadowThickness}px ${subtitleShadowColor}`;
    }

    if (subtitleBackgroundOpacity > 0) {
        const opacity = subtitleBackgroundOpacity;
        const color = subtitleBackgroundColor;
        const { r, g, b } = hexToRgb(color);
        styles['backgroundColor'] = `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    if (subtitleFontFamily && subtitleFontFamily.length > 0) {
        styles['fontFamily'] = `'${subtitleFontFamily}'`;
    }

    for (const customStyle of subtitleCustomStyles) {
        let key;

        if (
            customStyle.key.startsWith('webkit') ||
            customStyle.key.startsWith('moz') ||
            customStyle.key.startsWith('ms') ||
            /^o[A-Z].*/.test(customStyle.key)
        ) {
            key = customStyle.key.charAt(0).toUpperCase() + customStyle.key.slice(1);
        } else {
            key = customStyle.key;
        }

        if (!isNumeric(key)) {
            // A bug has allowed style keys that look like '0', '1',... to make it into some users' settings
            // Using such a style key with react crashes the app, so filter them out here
            styles[key] = customStyle.value;
        }
    }

    return styles;
}

export function isNumeric(str: string) {
    return !isNaN(Number(str));
}

export const HAS_LETTER_REGEX = /\p{L}/u;

export const NEWLINES_REGEX = /\r?\n/g;

export const STERM_AND_NEWLINES_REGEX = /(?:\p{STerm}|\r?\n)+/u;

export const ONLY_ASCII_LETTERS_REGEX = /^[a-z]+$/i;

const KANA_ONLY_REGEX =
    /^[\u3040-\u309F\u30A0-\u30FF\u31F0-\u31FF\uFF61-\uFF9F\u{1B000}-\u{1B0FF}\u{1B100}-\u{1B12F}\u{1B130}-\u{1B16F}\u{1AFF0}-\u{1AFFF}]+$/u;
export function isKanaOnly(text: string) {
    return KANA_ONLY_REGEX.test(text.normalize('NFC'));
}

const KATAKANA_ONLY_REGEX =
    /^(?:[\u30A0-\u30FF\u31F0-\u31FF\uFF61-\uFF9F\u{1B000}-\u{1B0FF}\u{1B100}-\u{1B12F}\u{1B130}-\u{1B16F}\u{1AFF0}-\u{1AFFF}]|\u3099|\u309A)+$/u;
export function isKatakanaOnly(text: string) {
    return KATAKANA_ONLY_REGEX.test(text.normalize('NFC'));
}

const SMALL_KANAS = [
    'ぁ',
    'ぃ',
    'ぅ',
    'ぇ',
    'ぉ',
    'ゃ',
    'ゅ',
    'ょ',
    'ゎ',
    'ァ',
    'ィ',
    'ゥ',
    'ェ',
    'ォ',
    'ャ',
    'ュ',
    'ョ',
    'ヮ',
];

export function getKanaMoras(kana: string): string[] {
    const moras: string[] = [];
    for (const char of kana.normalize('NFC')) {
        if (SMALL_KANAS.includes(char) && moras.length > 0) moras[moras.length - 1] += char;
        else moras.push(char);
    }
    return moras;
}

export function isKanaMoraPitchHigh(index: number, positions: PitchAccentPosition): boolean {
    if (typeof positions === 'string') return positions[index] === 'H';
    if (positions === 0) return index > 0;
    if (positions === 1) return index < 1;
    return index > 0 && index < positions;
}

export function isAttachedParticlePitchHigh(
    candidateText: string | undefined,
    prevPitch: PitchAccentContext
): boolean | null {
    if (candidateText?.length !== 1 || !isKanaOnly(candidateText)) return null;
    const { prevMoras, prevPitchAccent } = prevPitch;
    if (!prevMoras?.length || prevPitchAccent === undefined) return null;
    if (typeof prevPitchAccent === 'number') return isKanaMoraPitchHigh(prevMoras.length, prevPitchAccent); // Position as a number is handled naturally even with out-of-range mora counts
    if (prevPitchAccent.length > prevMoras.length) return isKanaMoraPitchHigh(prevMoras.length, prevPitchAccent); // Position explicitly includes pitch for attaching particles
    if (prevPitchAccent.length) return isKanaMoraPitchHigh(prevMoras.length - 1, prevPitchAccent); // Default to the same pitch as the last mora
    return null;
}

export interface PitchAccentContext {
    prevMoras?: string[];
    prevPitchAccent?: PitchAccentPosition;
    prevPitchHigh?: boolean;
}
export function clearPitchAccentContext(pitchCtx: PitchAccentContext) {
    pitchCtx.prevMoras = undefined;
    pitchCtx.prevPitchAccent = undefined;
    pitchCtx.prevPitchHigh = undefined;
}

export function normalizeForSearch(text: string): string {
    return text
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/ß/g, 'ss')
        .replace(/ẞ/g, 'SS')
        .replace(/æ/g, 'ae')
        .replace(/Æ/g, 'AE')
        .replace(/œ/g, 'oe')
        .replace(/Œ/g, 'OE')
        .replace(/ø/g, 'o')
        .replace(/Ø/g, 'O')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/ł/g, 'l')
        .replace(/Ł/g, 'L')
        .normalize('NFC');
}

export function normalizeSearchText(text: string): string {
    return text.normalize('NFKC').trim().toLocaleLowerCase();
}

export function normalizedLookupTerms(...texts: Array<string | null | undefined>): string[] {
    return Array.from(
        new Set(
            texts
                .flatMap((text) => {
                    if (!text) return [];
                    const normalized = normalizeForSearch(text);
                    if (!normalized.length || normalized === text) return [text];
                    return [text, normalized];
                })
                .filter((text) => Boolean(text))
                .map(normalizeSearchText)
                .filter((text) => text.length)
        )
    );
}

// https://stackoverflow.com/questions/63116039/camelcase-to-kebab-case
function kebabize(str: string) {
    const kebabized = str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '-' : '') + $.toLowerCase());

    if (
        kebabized.startsWith('webkit-') ||
        kebabized.startsWith('moz-') ||
        kebabized.startsWith('ms-') ||
        kebabized.startsWith('o-')
    ) {
        return `-${kebabized}`;
    }

    return kebabized;
}

export function computeStyleString(
    styleSettings: TextSubtitleSettings,
    values: { [key: string]: string | number } = {}
) {
    const stylesMap = computeStyles(styleSettings, values);
    const styleList = [];

    for (const [key, value] of Object.entries(stylesMap)) {
        styleList.push(`${kebabize(key)}: ${value} !important`);
    }

    return styleList.join(';');
}

// https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
export function hexToRgb(hex: string): Rgb {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (!result) {
        return { r: 255, g: 255, b: 255 };
    }

    return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    };
}

export function hex2ToPercent(hex: string): number {
    const parsed = Number.parseInt(hex.replace('#', ''), 16);
    if (!Number.isFinite(parsed)) return 1;
    return Math.max(0, Math.min(255, parsed)) / 255;
}

export function percentToHex2(percent: number): string {
    const hex = Math.round(Math.max(0, Math.min(1, percent)) * 255)
        .toString(16)
        .toUpperCase();
    return hex.length === 1 ? `0${hex}` : hex;
}

export function sourceString(subtitleFileName: string, timestamp: number) {
    return timestamp === 0 ? subtitleFileName : `${subtitleFileName} (${humanReadableTime(timestamp, true, true)})`;
}

export function buildSubtitleTracks(subtitles: { track: number }[], subtitleFileNames: string[]): SubtitleTrack[] {
    const trackNumbers = [...new Set(subtitles.map((s) => s.track))].sort((a, b) => a - b);
    return trackNumbers.map((trackNumber) => ({ trackNumber, fileName: subtitleFileNames[trackNumber] ?? '' }));
}

export function seekWithNudge(media: HTMLMediaElement, timestampSeconds: number) {
    timestampSeconds = clampMediaTimestamp(timestampSeconds, media.duration);
    media.currentTime = timestampSeconds;

    if (media.currentTime < timestampSeconds) {
        // Seeking is imprecise and may not land on the desired timestamp
        // Favor seeking slightly ahead to avoid getting stuck when seeking between subtitles
        media.currentTime = Math.min(media.duration, media.currentTime + 0.01);
    }

    return media.currentTime;
}

export async function inBatches<T>(
    items: T[],
    cb: (batch: T[]) => Promise<void>,
    options: { batchSize: number; statusUpdates?: (progress: Progress) => Promise<void> } = { batchSize: 5 }
): Promise<void> {
    const batchSize = options.batchSize > 0 ? options.batchSize : 1;
    let current = 0;
    const total = items.length;
    const startedAt = Date.now();
    for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        await cb(batch);
        if (options.statusUpdates) {
            current += batch.length;
            await options.statusUpdates({ current, total, startedAt });
        }
    }
}

export async function fromBatches<T, R>(
    items: T[],
    cb: (batch: T[]) => Promise<R[]>,
    options: { batchSize: number; statusUpdates?: (progress: Progress) => Promise<void> } = { batchSize: 5 }
): Promise<R[]> {
    const batchSize = options.batchSize > 0 ? options.batchSize : 1;
    const results: R[] = [];
    let current = 0;
    const total = items.length;
    const startedAt = Date.now();
    for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        const res = await cb(batch);
        for (let j = 0; j < res.length; j++) results.push(res[j]);
        if (options.statusUpdates) {
            current += batch.length;
            await options.statusUpdates({ current, total, startedAt });
        }
    }
    return results;
}

export async function mapAsync<T, R>(
    arr: T[],
    cb: (e: T) => Promise<R>,
    options: { batchSize: number; statusUpdates?: (progress: Progress) => Promise<void> } = { batchSize: 5 }
): Promise<R[]> {
    return fromBatches(arr, (batch) => Promise.all(batch.map(cb)), options);
}

export async function filterAsync<T>(
    arr: T[],
    cb: (e: T) => Promise<boolean>,
    options: { batchSize: number; statusUpdates?: (progress: Progress) => Promise<void> } = { batchSize: 5 }
): Promise<T[]> {
    const results = await mapAsync(arr, cb, options);
    return arr.filter((_, index) => results[index]);
}

export async function ensureStoragePersisted(): Promise<boolean | undefined> {
    if (!navigator.storage?.persist) return;
    if (await navigator.storage.persisted()) return true;
    const persisted = await navigator.storage.persist();
    if (!persisted) asbWarn('storage', 'Storage could not be persisted, data may be cleared by the browser');
    return persisted;
}

type Block = {
    pos: number[];
};

/**
 * Iterates over a string in "blocks" where a "block" represents a collection of substrings of the passed-in string.
 * @param str The string to iterate over.
 * @param block Function representing the substrings to iterate over.
 * @param callback Called when iterating over each block, and also gaps between blocks. When iterating over a gap,
 * the optional block argument is undefined.
 */
export function iterateOverStringInBlocks<B extends Block>(
    str: string,
    block: (str: string, blockIndex: number) => B | undefined,
    callback: (left: number, right: number, block?: B) => void
) {
    let left = 0;
    let right = 0;
    let currBlock = block(str, 0);

    if (currBlock === undefined) {
        callback(0, str.length);
        return;
    }

    for (let blockIndex = 0; right < str.length; ) {
        if (currBlock !== undefined && right === currBlock.pos[0]) {
            if (right > left) {
                callback(left, right);
            }
            callback(currBlock.pos[0], currBlock.pos[1], currBlock);
            blockIndex++;
            left = currBlock.pos[1];
            currBlock = block(str, blockIndex);
        }
        right = currBlock?.pos?.[0] ?? str.length;
    }

    if (left < right) {
        callback(left, right);
    }
}

/** Combines a token's readings with any unannotated spans into one searchable/displayable reading. */
export const getContiguousReading = (tokenText: string, token: Pick<Token, 'readings'>): string => {
    let readingText = '';
    iterateOverStringInBlocks(
        tokenText,
        (_, blockIndex) => token.readings[blockIndex],
        (left, right, reading) => {
            readingText += reading === undefined ? tokenText.substring(left, right) : reading.reading;
        }
    );
    return readingText;
};

type DimensionsComparators = {
    [K in keyof DimensionsModel]: (a: DimensionsModel[K], b: DimensionsModel[K]) => boolean;
};

const dimensionsComparators: DimensionsComparators = {
    width: (a, b) => a === b,
    height: (a, b) => a === b,
};

function compareDimensionsField<K extends keyof DimensionsModel>(key: K, a: DimensionsModel, b: DimensionsModel) {
    return dimensionsComparators[key](a[key], b[key]);
}

function areDimensionsEqual(a: DimensionsModel, b: DimensionsModel): boolean {
    if (a === b) return true;
    for (const key in dimensionsComparators) {
        if (!compareDimensionsField(key as keyof DimensionsModel, a, b)) return false;
    }
    return true;
}

type SubtitleTextImageComparators = {
    [K in keyof SubtitleTextImage]: (a: SubtitleTextImage[K], b: SubtitleTextImage[K]) => boolean;
};

const subtitleTextImageComparators: SubtitleTextImageComparators = {
    dataUrl: (a, b) => a === b,
    screen: (a, b) => areDimensionsEqual(a, b),
    image: (a, b) => areDimensionsEqual(a, b),
};

function compareSubtitleTextImageField<K extends keyof SubtitleTextImage>(
    key: K,
    a: SubtitleTextImage,
    b: SubtitleTextImage
) {
    return subtitleTextImageComparators[key](a[key], b[key]);
}

function areSubtitleTextImagesEqual(a: SubtitleTextImage | undefined, b: SubtitleTextImage | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    for (const key in subtitleTextImageComparators) {
        if (!compareSubtitleTextImageField(key as keyof SubtitleTextImage, a, b)) return false;
    }
    return true;
}

type SubtitleModelComparators = {
    [K in keyof SubtitleModel]: (a: SubtitleModel[K], b: SubtitleModel[K]) => boolean;
};

const subtitleModelComparators: SubtitleModelComparators = {
    text: (a, b) => a === b,
    originalText: (a, b) => a === b,
    textImage: (a, b) => areSubtitleTextImagesEqual(a, b),
    start: (a, b) => a === b,
    end: (a, b) => a === b,
    originalStart: (a, b) => a === b,
    originalEnd: (a, b) => a === b,
    displayTime: (a, b) => a === b,
    displayEndTime: (a, b) => a === b,
    track: (a, b) => a === b,
    index: (a, b) => a === b,
    tokenization: (a, b) => areTokenizationsEqual(a, b),
} satisfies Required<SubtitleModelComparators>;

export function compareSubtitleModelField<K extends keyof SubtitleModel>(
    key: K,
    a: SubtitleModel,
    b: SubtitleModel
): boolean {
    return subtitleModelComparators[key]!(a[key], b[key]);
}

export function areSubtitleModelsEqual(a: SubtitleModel, b: SubtitleModel): boolean {
    if (a === b) return true;
    for (const key in subtitleModelComparators) {
        if (!compareSubtitleModelField(key as keyof SubtitleModel, a, b)) return false;
    }
    return true;
}

export function areTokenizationsEqual(a: Tokenization | undefined, b: Tokenization | undefined) {
    if (a === b) return true;
    if (!a || !b) return false;

    if (a.error !== b.error) return false;
    return arrayEquals(a.tokens, b.tokens, areTokensEqual);
}

type TokenReadingComparators = {
    [K in keyof TokenReading]: (a: TokenReading[K], b: TokenReading[K]) => boolean;
};

const tokenReadingComparators: TokenReadingComparators = {
    pos: (a, b) => arrayEquals(a, b),
    reading: (a, b) => a === b,
} satisfies Required<TokenReadingComparators>;

function compareTokenReadingField<K extends keyof TokenReading>(key: K, a: TokenReading, b: TokenReading): boolean {
    return tokenReadingComparators[key](a[key], b[key]);
}

const areTokenReadingsEqual = (a: TokenReading, b: TokenReading) => {
    if (a === b) return true;
    for (const key in tokenReadingComparators) {
        if (!compareTokenReadingField(key as keyof TokenReading, a, b)) {
            return false;
        }
    }
    return true;
};

type TokenComparators = {
    [K in keyof Token]: (a: Token[K], b: Token[K]) => boolean;
};

const tokenComparators: TokenComparators = {
    pos: (a, b) => arrayEquals(a, b),
    states: (a, b) => arrayEquals(a, b),
    status: (a, b) => a === b,
    readings: (a, b) => arrayEquals(a, b, areTokenReadingsEqual),
    frequency: (a, b) => a === b,
    gloss: (a, b) => a === b,
    pitchAccent: (a, b) => a === b,
    groupingKey: (a, b) => a === b,
    lemmasGroupingKey: (a, b) => a === b,
    externalCandidateStatuses: (a, b) => arrayEquals(a, b),
} satisfies Required<TokenComparators>;

function compareTokenField<K extends keyof Token>(key: K, a: Token, b: Token): boolean {
    return tokenComparators[key]!(a[key], b[key]);
}

const areTokensEqual = (aToken: Token, bToken: Token) => {
    if (aToken === bToken) return true;
    for (const key in tokenComparators) {
        if (!compareTokenField(key as keyof Token, aToken, bToken)) {
            return false;
        }
    }
    return true;
};

/**
 * We prefer the highest status for a given token (e.g. duplicate anki cards)
 */
export function getTokenStatus(
    statuses: TokenStatusInfo[],
    dictionaryAnkiTreatSuspended: TokenStatus | 'NORMAL'
): TokenStatus {
    if (statuses.length && dictionaryAnkiTreatSuspended !== 'NORMAL') {
        const unsuspended = statuses.filter((status) => !status.suspended);
        if (!unsuspended.length) return dictionaryAnkiTreatSuspended;
        statuses = unsuspended;
    }
    if (statuses.some((c) => c.status === TokenStatus.MATURE)) return TokenStatus.MATURE;
    if (statuses.some((c) => c.status === TokenStatus.YOUNG)) return TokenStatus.YOUNG;
    if (statuses.some((c) => c.status === TokenStatus.GRADUATED)) return TokenStatus.GRADUATED;
    if (statuses.some((c) => c.status === TokenStatus.LEARNING)) return TokenStatus.LEARNING;
    return TokenStatus.UNKNOWN;
}

export function dedupeTokenStatusInfos(statuses: TokenStatusInfo[]): TokenStatusInfo[] | undefined {
    if (!statuses.length) return;
    const seen = new Set<string>();
    const deduped: TokenStatusInfo[] = [];
    for (const status of statuses) {
        const key = JSON.stringify([
            status.cardId,
            status.waniKani?.subjectId,
            status.waniKani?.subjectLevel,
            status.waniKani?.assignmentId,
            status.waniKani?.availableAt,
            status.status,
            status.suspended,
        ]);
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(status);
    }
    return deduped;
}

/**
 * Normalize a string for dictionary lookup, currently only by case folding.
 * If normalization expands from just case folding then the entire annotation and db logic will need
 * to be revisited since Dexie supports case folding natively through anyOfIgnoreCase() but nothing custom.
 */
export function normalizeToken(value: string): string {
    return value.toLowerCase();
}

/**
 * An async safe semaphore implementation that preserves FIFO order (within a priority group).
 * Priority levels are set with acquire(), higher numbers indicate a higher priority.
 * It uses an id for release to allow multiple releases (e.g try/finally with early releases).
 * @param options.permits The number of concurrent permits.
 * @param options.lifetimeMs Maximum lifetime of an acquire before automatic release (prevents deadlocks if callers don't call this.release()).
 */
export class AsyncSemaphore {
    private permits: number;
    private lifetimeMs?: number;
    private acquired: Set<number> = new Set();
    private timers: Map<number, ReturnType<typeof setTimeout>> = new Map();
    private waiting: Map<number, ((id: number) => void)[]> = new Map();
    private counter: number = 0;
    private getNextId = () => {
        if (this.counter === Number.MAX_SAFE_INTEGER) this.counter = 0;
        return ++this.counter;
    };

    constructor(options: { permits: number; lifetimeMs?: number }) {
        this.permits = Math.floor(options.permits);
        if (this.permits <= 0) {
            throw new Error('Permits count must be positive');
        }
        this.lifetimeMs = options.lifetimeMs;
        if (this.lifetimeMs && this.lifetimeMs <= 0) {
            throw new Error('Lifetime must be positive');
        }
    }

    private _acquire(): number {
        const id = this.getNextId();
        this.acquired.add(id);
        if (this.lifetimeMs) {
            this.timers.set(
                id,
                setTimeout(() => this.release(id), this.lifetimeMs)
            );
        }
        return id;
    }

    acquire(priority: number = 0): Promise<number> {
        return new Promise<number>((resolve) => {
            if (this.permits > 0) {
                this.permits--;
                resolve(this._acquire());
            } else {
                const queue = this.waiting.get(priority);
                if (queue) queue.push(resolve);
                else this.waiting.set(priority, [resolve]);
            }
        });
    }

    release(id: number): void {
        if (!this.acquired.has(id)) return;
        this.acquired.delete(id);
        clearTimeout(this.timers.get(id));
        this.timers.delete(id);

        if (this.waiting.size > 0) {
            const highestPriority = Math.max(...this.waiting.keys());
            const queue = this.waiting.get(highestPriority)!;
            queue.shift()!(this._acquire());
            if (!queue.length) this.waiting.delete(highestPriority);
        } else {
            this.permits++;
        }
    }
}
